import {
  QUELLDATEN_VARIABLE_REGEX,
  quelldatenVariablen,
  unbekannteQuelldatenVariablen,
} from '@core/domain';
import MonacoEditor, { loader, type Monaco } from '@monaco-editor/react';
import type * as monaco from 'monaco-editor';
import React, { useCallback } from 'react';

loader.config({ 'vs/nls': { availableLanguages: { '*': 'de' } } });

const LANGUAGE = 'opm-anmerkung';
const MARKER_OWNER = 'opm-anmerkung-variablen';

let registriert = false;

// Sprache und Vorschläge sind in Monaco global, daher nur einmal registrieren.
const registriereSprache = (m: Monaco) => {
  if (registriert) return;
  registriert = true;

  m.languages.register({ id: LANGUAGE });
  m.languages.setMonarchTokensProvider(LANGUAGE, {
    tokenizer: { root: [[/\$\{[^}]*\}/, 'variable']] },
  });
  m.editor.defineTheme(LANGUAGE, {
    base: 'vs',
    inherit: true,
    rules: [{ token: 'variable', foreground: '0f766e', fontStyle: 'bold' }],
    colors: {},
  });

  m.languages.registerCompletionItemProvider(LANGUAGE, {
    triggerCharacters: ['{', '.'],
    provideCompletionItems: (model, position) => {
      const zeileBisCursor = model
        .getLineContent(position.lineNumber)
        .slice(0, position.column - 1);
      const offen = /\$\{([\w.]*)$/.exec(zeileBisCursor);
      if (!offen) return { suggestions: [] };

      const schliesst =
        model.getLineContent(position.lineNumber)[position.column - 1] === '}';
      const range = new m.Range(
        position.lineNumber,
        position.column - offen[1].length,
        position.lineNumber,
        position.column,
      );
      return {
        suggestions: quelldatenVariablen().map((v) => ({
          label: v.pfad,
          kind: m.languages.CompletionItemKind.Variable,
          detail: v.typ,
          documentation: v.beschreibung,
          insertText: schliesst ? v.pfad : `${v.pfad}}`,
          range,
        })),
      };
    },
  });
};

const markiereUnbekannteVariablen = (
  m: Monaco,
  model: monaco.editor.ITextModel,
) => {
  const markers = [...model.getValue().matchAll(QUELLDATEN_VARIABLE_REGEX)]
    .filter((match) => unbekannteQuelldatenVariablen(match[0]).length > 0)
    .map((match) => {
      const start = model.getPositionAt(match.index);
      const end = model.getPositionAt(match.index + match[0].length);
      return {
        startLineNumber: start.lineNumber,
        startColumn: start.column,
        endLineNumber: end.lineNumber,
        endColumn: end.column,
        message: `Unbekannte Variable: ${match[0]}`,
        severity: m.MarkerSeverity.Error,
      };
    });
  m.editor.setModelMarkers(model, MARKER_OWNER, markers);
};

export const AnmerkungTextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  ariaLabel?: string;
  /** Einzeiliges Feld, z.B. für den Betreff. */
  singleLine?: boolean;
}> = ({
  value,
  onChange,
  onBlur,
  readOnly = false,
  ariaLabel,
  singleLine = false,
}) => {
  const onMount = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor, m: Monaco) => {
      if (singleLine) {
        // Enter übernimmt nur einen offenen Vorschlag, sonst kein Zeilenumbruch.
        editor.createContextKey('opmSingleLine', true);
        editor.addCommand(
          m.KeyCode.Enter,
          () => {
            // Enter schluckt den Zeilenumbruch.
          },
          'opmSingleLine && !suggestWidgetVisible',
        );
      }
      const model = editor.getModel();
      if (model) markiereUnbekannteVariablen(m, model);
      editor.onDidChangeModelContent(() => {
        const model = editor.getModel();
        if (model) markiereUnbekannteVariablen(m, model);
      });
      if (onBlur) editor.onDidBlurEditorText(onBlur);
    },
    [onBlur, singleLine],
  );

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden bg-white">
      <MonacoEditor
        height={singleLine ? '38px' : '120px'}
        language={LANGUAGE}
        theme={LANGUAGE}
        value={value}
        beforeMount={registriereSprache}
        onMount={onMount}
        onChange={(v) =>
          onChange(singleLine ? (v ?? '').replace(/\r?\n/g, ' ') : (v ?? ''))
        }
        options={{
          readOnly,
          domReadOnly: readOnly,
          ariaLabel,
          lineNumbers: 'off',
          glyphMargin: false,
          folding: false,
          minimap: { enabled: false },
          wordWrap: singleLine ? 'off' : 'on',
          scrollbar: singleLine
            ? { vertical: 'hidden', horizontal: 'hidden' }
            : undefined,
          overviewRulerLanes: singleLine ? 0 : undefined,
          wordBasedSuggestions: 'off',
          scrollBeyondLastLine: false,
          renderLineHighlight: 'none',
          automaticLayout: true,
          fixedOverflowWidgets: true,
          fontSize: 14,
          padding: { top: 8, bottom: 8 },
        }}
      />
    </div>
  );
};
